import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useBaseMutation } from 'src/api/core/useBaseQuery';
import { useAppSelector } from 'src/app/hooks';
import { toLocalDate } from 'src/utils/date/dateHelper';
import { NursePatient, NursePatientListResponse } from 'src/modules/nurse/patient-list/pages/list/models';
import { useSelectedOffice } from 'src/hooks/useSelectedOffice';

const poppins = 'Poppins, sans-serif';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface FilterCategory {
    id: string;
    label: string;
    count: number;
    icon: string;
}

interface FilterChip {
    id: string;
    label: string;
}

// ─── Mock filter data ──────────────────────────────────────────────────────────

export const CATEGORIES: FilterCategory[] = [
    { id: 'ilac', label: 'İlaç Tedavileri', count: 4, icon: '/assets/icons/work-list/work-list-order-medicine.svg?v=2' },
    { id: 'bakim', label: 'Bakım Tedavileri', count: 2, icon: '/assets/icons/work-list/care-plans.svg?v=2' },
    { id: 'diger', label: 'Diğer Tedavileri', count: 2, icon: '/assets/icons/work-list/work-list-other-medicine.svg?v=2' },
    { id: 'kan', label: 'Kan Tedavileri', count: 1, icon: '/assets/icons/work-list/blond-request.svg?v=2' },
    { id: 'ameliyat', label: 'Ameliyathane', count: 3, icon: '/assets/icons/work-list/operating-room.svg?v=2' },
    { id: 'tetkik', label: 'Tetkik Hazırlık', count: 2, icon: '/assets/icons/work-list/Radyoloji.svg?v=2' },
    { id: 'devir', label: 'Hasta Devir', count: 1, icon: '/assets/icons/work-list/patient-handover.svg?v=2' },
    { id: 'plan', label: 'Plan Bekleyen', count: 1, icon: '/assets/icons/work-list/waiting-plan.svg?v=2' },
    { id: 'hizmet', label: 'Hizmet İsteği', count: 1, icon: '/assets/icons/work-list/service-request.svg?v=2' },
];

type WorkListFilterOptions = {
    floors: FilterChip[];
    departments: FilterChip[];
    doctors: FilterChip[];
    patients: FilterChip[];
};

export interface WorkListFilterSnapshot {
    categories: string[];
    floors: string[];
    departments: string[];
    doctors: string[];
    patients: string[];
}

const WORK_LIST_FILTER_STORAGE_KEY = 'nurse-work-list-filters';

const uniqBy = <T,>(items: T[], keyFn: (i: T) => string): T[] => {
    const seen = new Set<string>();
    const out: T[] = [];
    for (const item of items) {
        const key = keyFn(item);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(item);
    }
    return out;
};

const normalizeText = (v: unknown): string => {
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v).trim();
    return '';
};

const buildFilterOptionsFromPatients = (patients: NursePatientListResponse | undefined | null): WorkListFilterOptions => {
    const rows: NursePatient[] = Array.isArray(patients) ? patients : [];

    const floors = uniqBy(
        rows
            .map((p) => normalizeText(p.floorinfo))
            .filter(Boolean)
            .map((label) => ({ id: label, label })),
        (x) => x.id
    ).sort((a, b) => a.label.localeCompare(b.label, 'tr'));

    const departments = uniqBy(
        rows
            .map((p) => normalizeText(p.deptname))
            .filter(Boolean)
            .map((label) => ({ id: label, label })),
        (x) => x.id
    ).sort((a, b) => a.label.localeCompare(b.label, 'tr'));

    const doctors = uniqBy(
        rows
            .map((p) => {
                const doctorName = normalizeText(p.drname);
                const label = doctorName || '';
                const id = label;
                return { id, label };
            })
            .filter((x) => Boolean(x.id) && Boolean(x.label)),
        (x) => x.id
    ).sort((a, b) => a.label.localeCompare(b.label, 'tr'));

    const patientsChips = uniqBy(
        rows
            .map((p) => {
                const label = `${normalizeText(p.name)} ${normalizeText(p.surname)}`.trim();
                const id = label;
                return { id, label };
            })
            .filter((x) => Boolean(x.id) && Boolean(x.label)),
        (x) => x.id
    ).sort((a, b) => a.label.localeCompare(b.label, 'tr'));

    return {
        floors,
        departments,
        doctors,
        patients: patientsChips,
    };
};

// ─── Chip component ────────────────────────────────────────────────────────────

const FilterChipItem: React.FC<{
    label: string;
    selected: boolean;
    onToggle: () => void;
}> = ({ label, selected, onToggle }) => (
    <Box
        onClick={onToggle}
        sx={{
            borderRadius: '100px',
            border: `1px solid ${selected ? '#2196F3' : '#ECEDF2'}`,
            background: selected ? 'rgba(33, 150, 243, 0.05)' : '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '6px 14px',
            cursor: 'pointer',
            transition: 'all 0.15s',
            '&:hover': {
                border: '1px solid #2196F3',
                background: 'rgba(33, 150, 243, 0.04)',
            },
        }}
    >
        <Box sx={{ fontFamily: poppins, fontSize: '12px', lineHeight: '24px', color: selected ? '#2196F3' : '#333', fontWeight: selected ? 500 : 400 }}>
            {label}
        </Box>
    </Box>
);

// ─── Section header with count badge ──────────────────────────────────────────

const SectionHeader: React.FC<{
    title: string;
    count: number;
    showClear?: boolean;
    onClear?: () => void;
}> = ({ title, count, showClear, onClear }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Box sx={{ fontFamily: poppins, fontSize: '15px', fontWeight: 600, color: '#000', lineHeight: '24px' }}>
            {title}
        </Box>
        <Box
            sx={{
                borderRadius: '24px',
                background: 'rgba(219, 234, 254, 0.5)',
                padding: '2px 10px',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <Box sx={{ fontFamily: poppins, fontSize: '13px', fontWeight: 500, color: '#2196F3' }}>
                {count}
            </Box>
        </Box>
        {showClear && (
            <Box
                onClick={onClear}
                sx={{ fontFamily: poppins, fontSize: '12px', fontWeight: 500, color: '#2196F3', cursor: 'pointer', ml: 'auto', '&:hover': { opacity: 0.7 } }}
            >
                Temizle
            </Box>
        )}
    </Box>
);

// ─── Category icon item ────────────────────────────────────────────────────────

const CategoryIconItem: React.FC<{
    category: FilterCategory;
    selected: boolean;
    onToggle: () => void;
}> = ({ category, selected, onToggle }) => (
    <button
        type="button"
        onClick={onToggle}
        style={{
            width: 79,
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 8,
            display: 'inline-flex',
            cursor: 'pointer',
            background: 'transparent',
            border: 'none',
            padding: 0,
            font: 'inherit',
        }}
    >
        {/* Icon circle + badge */}
        <div style={{ width: 46, height: 46, position: 'relative' }}>
            {/* Circle card */}
            <div style={{
                width: 46,
                height: 46,
                padding: 12,
                left: 0,
                top: 0,
                position: 'absolute',
                background: selected ? 'rgba(33, 150, 243, 0.12)' : 'white',
                boxShadow: selected
                    ? '0px 4px 80px rgba(105, 111, 134, 0.25), 0 0 0 2px #2196F3'
                    : '0px 4px 80px rgba(105, 111, 134, 0.25)',
                borderRadius: 23,
                boxSizing: 'border-box',
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                transition: 'all 0.15s',
            }}>
                <img
                    src={category.icon}
                    alt={category.label}
                    style={{ width: 21, height: 21, objectFit: 'contain' }}
                    onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0'; }}
                />
            </div>
            {/* Badge */}
            <div style={{
                height: 15,
                left: 32,
                top: 0,
                position: 'absolute',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 5,
                display: 'inline-flex',
            }}>
                <div style={{
                    minWidth: 14,
                    paddingLeft: 4,
                    paddingRight: 4,
                    background: '#2196F3',
                    borderRadius: 8,
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 10,
                    display: 'inline-flex',
                    boxSizing: 'border-box',
                }}>
                    <div style={{
                        alignSelf: 'stretch',
                        textAlign: 'center',
                        color: 'white',
                        fontSize: 10,
                        fontFamily: 'Poppins',
                        fontWeight: '700',
                        wordWrap: 'break-word',
                    }}>{category.count}</div>
                </div>
            </div>
        </div>
        {/* Label */}
        <div style={{
            textAlign: 'center',
            color: 'black',
            fontSize: 9,
            fontFamily: 'Poppins',
            fontWeight: '300',
            lineHeight: '11.7px',
            wordWrap: 'break-word',
        }}>{category.label}</div>
    </button>
);

// ─── Divider ───────────────────────────────────────────────────────────────────

const Divider = () => (
    <Box sx={{ width: '100%', height: '0.5px', background: '#D5D5D5', my: '2px' }} />
);

// ─── Main FilterModal ──────────────────────────────────────────────────────────

export interface WorkListFilterModalProps {
    open: boolean;
    onClose: () => void;
    onApply: (filters: WorkListFilterSnapshot) => void;
    /** Worklist'teki gerçek iş sayıları (kategori id -> adet) */
    categoryCounts?: Record<string, number>;
}

const WorkListFilterModal: React.FC<WorkListFilterModalProps> = ({ open, onClose, onApply, categoryCounts }) => {
    const { user } = useAppSelector((state) => state.auth);
    const { effectiveOffId } = useSelectedOffice();

    const [startDate] = useState<string>(() => toLocalDate(new Date()));
    const [endDate] = useState<string>(() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        return toLocalDate(d);
    });

    const patientListMutation = useBaseMutation<NursePatientListResponse>({
        endpoint: '/nurse/api/Nurse/GetNurseProcessList',
        method: 'POST',
        data: {
            offId: effectiveOffId,
            userId: user?.id,
            hfId: 0,
            deptId: 0,
            startTime: startDate,
            endTime: endDate,
            isDischarge: '0',
            nurseId: 0,
        },
    });

    const { data: nursePatients, mutate: fetchNursePatients, isLoading: isPatientsLoading } = patientListMutation as any;

    const options = useMemo(() => {
        const raw: any = nursePatients;
        const payload = raw && Array.isArray(raw.data) ? raw.data : nursePatients;
        return buildFilterOptionsFromPatients(payload);
    }, [nursePatients]);

    // Kategori ikonlarının üzerindeki sayıları worklist'ten gelen gerçek değerlerle güncelle
    const categoriesWithCounts: FilterCategory[] = useMemo(
        () =>
            CATEGORIES.map((cat) => ({
                ...cat,
                count: categoryCounts && typeof categoryCounts[cat.id] === 'number'
                    ? categoryCounts[cat.id]
                    : 0,
            })),
        [categoryCounts]
    );

    // Seçili id'ler: boş set = filtre yok (hepsi geçerli)
    const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
    const [selectedFloors, setSelectedFloors] = useState<Set<string>>(new Set());
    const [selectedDepartments, setSelectedDepartments] = useState<Set<string>>(new Set());
    const [selectedDoctors, setSelectedDoctors] = useState<Set<string>>(new Set());
    const [selectedPatients, setSelectedPatients] = useState<Set<string>>(new Set());

    // Load saved filters when modal first opens
    useEffect(() => {
        if (!open) return;
        try {
            const raw = localStorage.getItem(WORK_LIST_FILTER_STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw) as Partial<WorkListFilterSnapshot>;
            if (Array.isArray(parsed.categories)) setSelectedCategories(new Set(parsed.categories));
            if (Array.isArray(parsed.floors)) setSelectedFloors(new Set(parsed.floors));
            if (Array.isArray(parsed.departments)) setSelectedDepartments(new Set(parsed.departments));
            if (Array.isArray(parsed.doctors)) setSelectedDoctors(new Set(parsed.doctors));
            if (Array.isArray(parsed.patients)) setSelectedPatients(new Set(parsed.patients));
        } catch {
            // ignore storage errors
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (!effectiveOffId || !user?.id) return;
        fetchNursePatients();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, effectiveOffId, user?.id]);

    // Option listeleri değişince selection setlerini "stale" id'lerden arındır
    useEffect(() => {
        // Veri henüz gelmediyse, seçimleri silme
        if (
            options.floors.length === 0 &&
            options.departments.length === 0 &&
            options.doctors.length === 0 &&
            options.patients.length === 0
        ) {
            return;
        }

        const floorIds = new Set(options.floors.map((x) => x.id));
        const deptIds = new Set(options.departments.map((x) => x.id));
        const doctorIds = new Set(options.doctors.map((x) => x.id));
        const patientIds = new Set(options.patients.map((x) => x.id));

        setSelectedFloors((prev) => new Set(Array.from(prev).filter((id) => floorIds.has(id))));
        setSelectedDepartments((prev) => new Set(Array.from(prev).filter((id) => deptIds.has(id))));
        setSelectedDoctors((prev) => new Set(Array.from(prev).filter((id) => doctorIds.has(id))));
        setSelectedPatients((prev) => new Set(Array.from(prev).filter((id) => patientIds.has(id))));
    }, [options.departments, options.doctors, options.floors, options.patients]);

    // Persist filters whenever they change (boş set = filtre yok)
    useEffect(() => {
        const snapshot: WorkListFilterSnapshot = {
            categories: Array.from(selectedCategories),
            floors: Array.from(selectedFloors),
            departments: Array.from(selectedDepartments),
            doctors: Array.from(selectedDoctors),
            patients: Array.from(selectedPatients),
        };
        try {
            localStorage.setItem(WORK_LIST_FILTER_STORAGE_KEY, JSON.stringify(snapshot));
        } catch {
            // ignore storage errors
        }
    }, [selectedCategories, selectedFloors, selectedDepartments, selectedDoctors, selectedPatients]);

    const toggleSelected = (setFn: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) => {
        setFn((prev: Set<string>) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const clearAll = () => {
        // Hepsi boş = filtre yok, tüm datayı göster
        setSelectedCategories(new Set());
        setSelectedFloors(new Set());
        setSelectedDepartments(new Set());
        setSelectedDoctors(new Set());
        setSelectedPatients(new Set());
    };

    const getEffectiveCount = (selected: Set<string>, total: number) =>
        selected.size > 0 ? selected.size : total;

    const handleApplyClick = () => {
        const snapshot: WorkListFilterSnapshot = {
            categories: Array.from(selectedCategories),
            floors: Array.from(selectedFloors),
            departments: Array.from(selectedDepartments),
            doctors: Array.from(selectedDoctors),
            patients: Array.from(selectedPatients),
        };
        onApply(snapshot);
    };

    return (
        <>
            {/* Overlay */}
            <Box
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 10,
                    background: 'rgba(0,0,0,0.3)',
                    opacity: open ? 1 : 0,
                    pointerEvents: open ? 'all' : 'none',
                    transition: 'opacity 0.25s',
                }}
            />

            {/* Bottom sheet */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 11,
                    background: '#fff',
                    borderRadius: '20px 20px 0 0',
                    transform: open ? 'translateY(0)' : 'translateY(100%)',
                    transition: 'transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '88%',
                    boxShadow: '0px -8px 40px rgba(0,0,0,0.18)',
                    overflow: 'hidden',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header: Temizle | Filtreler | X */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '18px 20px 14px',
                        borderBottom: '1px solid #F6F7F9',
                        flexShrink: 0,
                    }}
                >
                    <Box
                        onClick={clearAll}
                        sx={{ fontFamily: poppins, fontSize: '14px', fontWeight: 500, color: '#2196F3', cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                    >
                        Temizle
                    </Box>
                    <Box sx={{ fontFamily: poppins, fontSize: '16px', fontWeight: 600, color: '#000', lineHeight: '24px' }}>
                        Filtreler
                    </Box>
                    <Box
                        onClick={onClose}
                        sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#555', borderRadius: '6px', '&:hover': { background: '#F5F5F5' } }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </Box>
                </Box>

                {/* Scrollable body */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '16px 20px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        '&::-webkit-scrollbar': { width: 3 },
                        '&::-webkit-scrollbar-thumb': { background: '#ddd', borderRadius: 2 },
                    }}
                >
                    {/* ── Kategori Seçimi ── */}
                    <div style={{ width: '100%', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 12, display: 'flex' }}>
                        {/* Header row */}
                        <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'inline-flex' }}>
                            <div style={{ color: 'black', fontSize: 18, fontFamily: 'Poppins', fontWeight: '600', lineHeight: '24px', wordWrap: 'break-word' }}>Kategori Seçimi</div>
                            <div style={{ paddingLeft: 14, paddingRight: 14, paddingTop: 2, paddingBottom: 2, background: 'rgba(219, 234, 254, 0.50)', borderRadius: 24, flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 8, display: 'inline-flex' }}>
                                <div style={{ opacity: 0.99, color: '#2196F3', fontSize: 14, fontFamily: 'Poppins', fontWeight: '500', wordWrap: 'break-word' }}>
                                    {getEffectiveCount(selectedCategories, CATEGORIES.length)}
                                </div>
                            </div>
                            {selectedCategories.size > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setSelectedCategories(new Set())}
                                    style={{ color: '#2196F3', fontSize: 12, fontFamily: 'Poppins', fontWeight: '500', lineHeight: '24px', wordWrap: 'break-word', cursor: 'pointer', marginLeft: 'auto', background: 'transparent', border: 'none', padding: 0 }}
                                >
                                    Temizle
                                </button>
                            )}
                        </div>
                        {/* Icon grid — 4 per row, wrapping */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                            {categoriesWithCounts.map((cat) => (
                                <CategoryIconItem
                                    key={cat.id}
                                    category={cat}
                                    selected={selectedCategories.has(cat.id)}
                                    onToggle={() => toggleSelected(setSelectedCategories, cat.id)}
                                />
                            ))}
                        </div>
                    </div>

                    <Divider />

                    {/* ── Kat Seçimi ── */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <SectionHeader
                            title="Kat Seçimi"
                            count={getEffectiveCount(selectedFloors, options.floors.length)}
                            showClear={selectedFloors.size > 0}
                            onClear={() => setSelectedFloors(new Set())}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {options.floors.map((chip) => (
                                <FilterChipItem
                                    key={chip.id}
                                    label={chip.label}
                                    selected={selectedFloors.has(chip.id)}
                                    onToggle={() => toggleSelected(setSelectedFloors, chip.id)}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Divider />

                    {/* ── Bölüm Seçimi ── */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <SectionHeader
                            title="Bölüm Seçimi"
                            count={getEffectiveCount(selectedDepartments, options.departments.length)}
                            showClear={selectedDepartments.size > 0}
                            onClear={() => setSelectedDepartments(new Set())}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {options.departments.map((chip) => (
                                <FilterChipItem
                                    key={chip.id}
                                    label={chip.label}
                                    selected={selectedDepartments.has(chip.id)}
                                    onToggle={() => toggleSelected(setSelectedDepartments, chip.id)}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Divider />

                    {/* ── Doktor Seçimi ── */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <SectionHeader
                            title="Doktor Seçimi"
                            count={getEffectiveCount(selectedDoctors, options.doctors.length)}
                            showClear={selectedDoctors.size > 0}
                            onClear={() => setSelectedDoctors(new Set())}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {options.doctors.map((chip) => (
                                <FilterChipItem
                                    key={chip.id}
                                    label={chip.label}
                                    selected={selectedDoctors.has(chip.id)}
                                    onToggle={() => toggleSelected(setSelectedDoctors, chip.id)}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Divider />

                    {/* ── Hasta Seçimi ── */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <SectionHeader
                            title="Hasta Seçimi"
                            count={getEffectiveCount(selectedPatients, options.patients.length)}
                            showClear={selectedPatients.size > 0}
                            onClear={() => setSelectedPatients(new Set())}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {options.patients.map((chip) => (
                                <FilterChipItem
                                    key={chip.id}
                                    label={chip.label}
                                    selected={selectedPatients.has(chip.id)}
                                    onToggle={() => toggleSelected(setSelectedPatients, chip.id)}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Loading hint (non-blocking) */}
                    {open && isPatientsLoading && (
                        <Box sx={{ fontFamily: poppins, fontSize: '12px', color: '#8B8B8B' }}>
                            Yükleniyor...
                        </Box>
                    )}

                    {/* CTA - Now scrollable with the rest of the content */}
                    <Box
                        sx={{
                            marginTop: '16px',
                            paddingTop: '16px',
                            borderTop: '1px solid #F6F7F9',
                        }}
                    >
                        <Box
                            onClick={handleApplyClick}
                            sx={{
                                width: '100%',
                                height: 52,
                                borderRadius: '48px',
                                background: '#2196F3',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'background 0.15s',
                                '&:hover': { background: '#1976D2' },
                                '&:active': { background: '#1565C0' },
                            }}
                        >
                            <Box sx={{ fontFamily: poppins, fontSize: '16px', fontWeight: 500, color: '#fff', lineHeight: '160%' }}>
                                Filtrele
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default WorkListFilterModal;
